import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LoadingButton } from '@mui/lab';
import {
	AppBar,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Stack,
	TextField,
	Toolbar,
	Typography
} from '@mui/material';
import { DialogTransition, FollowUpHistory } from 'components';
import { animated, useTransition } from '@react-spring/web';
import { useScreenType } from 'hooks';
import { EActionState } from 'models';
import { ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actionCreator, selectors } from 'store/followups/close';
import { theme } from 'theme';
import { pacsService } from 'services';

export const CloseFollowUpDialog = () => {
	const dispatch = useDispatch();
	const loading = useSelector(selectors.getInitAction);
	const open = useSelector(selectors.getOpen);
	const screenType = useScreenType();

	// Form state from Redux
	const reasonForNoPurchase = useSelector(selectors.form.getReasonForNoPurchase);
	const comments = useSelector(selectors.form.getComments);

	const handleCancel = () => {
		dispatch(actionCreator.INIT_FLUSH());
	};

	// Validation
	const fieldErrors = {
		reasonForNoPurchase: reasonForNoPurchase.newValue.length === 0,
		comments: pacsService.FollowUpCloseOpportunityNotesRequired && comments.newValue.trim().length === 0
	};
	const isFormValid = !fieldErrors.reasonForNoPurchase && !fieldErrors.comments;

	return (
		<Dialog
			open={open}
			fullScreen={screenType.isPhone}
			onClose={(event, reason) => {
				if (reason === 'backdropClick') return;
				handleCancel();
			}}
			fullWidth={true}
			maxWidth={'md'}
			TransitionComponent={DialogTransition}
			transitionDuration={{
				appear: theme.transitions.duration.enteringScreen,
				enter: theme.transitions.duration.enteringScreen,
				exit: 0
			}}>
			{screenType.isPhone && <TitleBar title='Close Opportunity' onClose={handleCancel} />}
			{screenType.isDesktop && <DialogTitle>Close Opportunity</DialogTitle>}
			<DialogContent sx={{ p: 0, minHeight: 420, overflow: 'hidden' }}>
				{loading === EActionState.RUNNING && (
					<Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
						<CircularProgress />
					</Box>
				)}
				{(() => {
					const show = loading === EActionState.COMPLETED;
					const transitions = useTransition(show, {
						from: { opacity: 0, transform: 'translateY(6px)' },
						enter: { opacity: 1, transform: 'translateY(0px)' },
						leave: { opacity: 0, transform: 'translateY(6px)' },
						config: { tension: 200, friction: 20 }
					});
					return transitions(
						(styles, item) =>
							item && (
								<animated.div style={{ ...styles, willChange: 'opacity, transform', display: 'block' }}>
									<Box sx={{ width: '100%', p: 2 }}>
										<Content fieldErrors={fieldErrors} />
									</Box>
								</animated.div>
							)
					);
				})()}
			</DialogContent>
			<DialogActions sx={{ justifyContent: 'flex-end', px: 2, py: 1.5 }}>
				<Stack spacing={1} direction='row' sx={{ width: screenType.isPhone ? '100%' : 'auto' }}>
					<CloseOpportunityButton isFormValid={isFormValid} />
					<Button color='neutral' variant='outlined' onClick={handleCancel} fullWidth={screenType.isPhone}>
						Cancel
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
};

interface ActionButtonProps {
	isFormValid: boolean;
}

const CloseOpportunityButton = (props: ActionButtonProps) => {
	const { isFormValid } = props;
	const dispatch = useDispatch();
	const closeAction = useSelector(selectors.getCloseAction);
	const screenType = useScreenType();

	const handleClick = () => {
		dispatch(actionCreator.CLOSE_START());
	};

	return (
		<LoadingButton
			color='error'
			variant='contained'
			onClick={handleClick}
			disabled={!isFormValid}
			loading={closeAction === EActionState.RUNNING}
			fullWidth={screenType.isPhone}>
			Close Opportunity
		</LoadingButton>
	);
};

const TitleBar = (props: { title: string; onClose: () => void }) => {
	const { title, onClose } = props;
	return (
		<AppBar position='static' color='inherit'>
			<Toolbar>
				<IconButton color='inherit' onClick={onClose}>
					<ArrowBackIcon />
				</IconButton>
				<Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
					{title}
				</Typography>
			</Toolbar>
		</AppBar>
	);
};

interface ContentProps {
	fieldErrors: {
		reasonForNoPurchase: boolean;
		comments: boolean;
	};
}

const Content = (props: ContentProps) => {
	const { fieldErrors } = props;
	const dispatch = useDispatch();
	const reasonForNoPurchase = useSelector(selectors.form.getReasonForNoPurchase);
	const comments = useSelector(selectors.form.getComments);
	const reasonCodes = useSelector(selectors.getReasonCodes);
	const followUps = useSelector(selectors.getFollowUps);

	const handleReasonChange = (event: SelectChangeEvent) => {
		dispatch(actionCreator.SET_REASON_FOR_NO_PURCHASE(event.target.value));
	};

	const handleCommentsChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
		const value = event.target.value;
		if (value.length <= 500) {
			dispatch(actionCreator.SET_COMMENTS(value));
		}
	};

	return (
		<Box sx={{ p: 2, pt: 0 }}>
			<FollowUpHistory items={followUps} sx={{ pb: 3 }} />
			<Typography variant='subtitle1' sx={{ pb: 1, fontWeight: 'bold' }}>
				Close Opportunity For Further Follow Up Activity
			</Typography>
			<Stack spacing={2}>
				{/* Reason For No Purchase */}
				<FormControl fullWidth error={fieldErrors.reasonForNoPurchase}>
					<InputLabel id='reason-for-no-purchase-label'>Reason For No Purchase</InputLabel>
					<Select
						labelId='reason-for-no-purchase-label'
						value={reasonForNoPurchase.newValue}
						label='Reason For No Purchase'
						onChange={handleReasonChange}>
						{reasonCodes.map(reason => (
							<MenuItem key={reason.value} value={reason.value}>
								{reason.displayText}
							</MenuItem>
						))}
					</Select>
					{fieldErrors.reasonForNoPurchase && <FormHelperText>Required</FormHelperText>}
				</FormControl>

				{/* Comments */}
				<Stack spacing={1}>
					<TextField
						label='Comments'
						multiline
						rows={8}
						fullWidth
						value={comments.newValue}
						onChange={handleCommentsChange}
						inputProps={{ maxLength: 250 }}
						error={fieldErrors.comments}
						helperText={fieldErrors.comments ? 'Required' : ''}
					/>
					<Typography variant='caption' sx={{ textAlign: 'right', color: 'text.secondary' }}>
						{comments.newValue.length} / 250
					</Typography>
				</Stack>
			</Stack>
		</Box>
	);
};
